import React, { useCallback, useEffect, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Calendar } from "primereact/calendar";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { SelectButton } from "primereact/selectbutton";
import { ProgressSpinner } from "primereact/progressspinner";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { toast } from "react-toastify";
import { getSessions } from "../../api/sessionApi";
import {
  getTerms,
  createTerm,
  updateTerm,
  deleteTerm,
  getTermWeeks,
  updateTermWeeks,
} from "../../api/termApi";
import { useCurrency, formatMoney } from "../../common/utils/currency";
import { useSchoolFeatures } from "../../common/utils/schoolSettings";
import { formatTime } from "../../common/utils/classTiming";
import { useNavigate } from "react-router-dom";
import "./TermsManager.scss";

// TIME column ("HH:MM:SS") <-> the "HH:MM" an <input type="time"> wants.
const toHm = (t) => (t ? String(t).slice(0, 5) : "");

// Built from local parts on purpose. The picker hands back local midnight, so
// toISOString() would shift the date back a day for any timezone ahead of UTC
// — picking 14 Aug in the UK would have saved 13 Aug.
const toIso = (d) => {
  if (!d) return null;
  const date = new Date(d);
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${m}-${day}`;
};
const fromIso = (s) => (s ? new Date(`${String(s).slice(0, 10)}T00:00:00`) : null);
const fmt = (s) =>
  s ? fromIso(s).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "";

const emptyTerm = {
  name: "",
  start_date: null,
  end_date: null,
  weekly_fee: null,
  sort_order: 0,
  start_time: "",
  end_time: "",
};

// Three-state week control. `null` hands the week back to the holiday calendar,
// which is the default and what an admin should land on almost every time.
const OVERRIDE_OPTIONS = [
  { label: "Auto", value: null, title: "Follow the holiday calendar" },
  { label: "On", value: true, title: "Force this week on regardless of the calendar" },
  { label: "Off", value: false, title: "Force this week off regardless of the calendar" },
];

const STATUS_TAG = {
  full: { severity: "success", label: "Full week" },
  partial: { severity: "warning", label: "Partial" },
  closed: { severity: "danger", label: "Closed" },
};

const TermsManager = () => {
  const currency = useCurrency();
  const { timingMode } = useSchoolFeatures();
  const perTermTiming = timingMode === "per_term";
  const navigate = useNavigate();

  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [terms, setTerms] = useState([]);
  const [loadingTerms, setLoadingTerms] = useState(false);

  const [selectedTerm, setSelectedTerm] = useState(null);
  const [weeks, setWeeks] = useState([]);
  const [weekSummary, setWeekSummary] = useState(null);
  const [loadingWeeks, setLoadingWeeks] = useState(false);
  const [dirtyWeeks, setDirtyWeeks] = useState({});
  const [savingWeeks, setSavingWeeks] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyTerm);
  const [saving, setSaving] = useState(false);

  // ── load sessions ──
  useEffect(() => {
    (async () => {
      try {
        const data = await getSessions();
        const list = data?.data || data || [];
        setSessions(list);
        const active = list.find((s) => s.is_active) || list[0];
        if (active) setSessionId(active.id);
      } catch {
        toast.error("Failed to load academic sessions");
      }
    })();
  }, []);

  const loadTerms = useCallback(async (sid) => {
    if (!sid) return;
    setLoadingTerms(true);
    try {
      const res = await getTerms(sid);
      setTerms(res?.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load terms");
    } finally {
      setLoadingTerms(false);
    }
  }, []);

  useEffect(() => {
    setSelectedTerm(null);
    setWeeks([]);
    setWeekSummary(null);
    setDirtyWeeks({});
    loadTerms(sessionId);
  }, [sessionId, loadTerms]);

  // ── weeks ──
  const loadWeeks = async (term) => {
    setSelectedTerm(term);
    setDirtyWeeks({});
    setLoadingWeeks(true);
    try {
      const res = await getTermWeeks(term.id);
      setWeeks(res?.data || []);
      setWeekSummary(res?.summary || null);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load weeks");
    } finally {
      setLoadingWeeks(false);
    }
  };

  /** Apply an override locally so the grid updates before the save round-trip. */
  const applyOverride = (week, value) => ({
    ...week,
    availability_override: value,
    // Mirror what the server will compute, so the summary line stays honest.
    is_available: value === null ? week.calendar_available : value,
  });

  const setWeekOverride = (week, value) => {
    setWeeks((prev) => prev.map((w) => (w.id === week.id ? applyOverride(w, value) : w)));
    setDirtyWeeks((prev) => ({
      ...prev,
      [week.id]: { id: week.id, availability_override: value },
    }));
  };

  const setAllWeeks = (value) => {
    setWeeks((prev) => prev.map((w) => applyOverride(w, value)));
    setDirtyWeeks(
      weeks.reduce(
        (acc, w) => ({ ...acc, [w.id]: { id: w.id, availability_override: value } }),
        {}
      )
    );
  };

  const saveWeeks = async () => {
    const payload = Object.values(dirtyWeeks);
    if (!payload.length) return;
    setSavingWeeks(true);
    try {
      const res = await updateTermWeeks(selectedTerm.id, payload);
      toast.success(`Saved — ${res.available_weeks} week(s) available`);
      setDirtyWeeks({});
      // Reload rather than patch: status, teaching days and the billable total
      // are all derived server-side and may have shifted.
      await loadWeeks(selectedTerm);
      await loadTerms(sessionId);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save weeks");
    } finally {
      setSavingWeeks(false);
    }
  };

  // ── term CRUD ──
  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyTerm, sort_order: terms.length });
    setDialogOpen(true);
  };

  const openEdit = (term) => {
    setEditingId(term.id);
    setForm({
      name: term.name,
      start_date: fromIso(term.start_date),
      end_date: fromIso(term.end_date),
      weekly_fee: term.weekly_fee != null ? Number(term.weekly_fee) : null,
      sort_order: term.sort_order ?? 0,
      start_time: toHm(term.start_time),
      end_time: toHm(term.end_time),
    });
    setDialogOpen(true);
  };

  const saveTerm = async () => {
    if (!form.name?.trim() || !form.start_date || !form.end_date) {
      toast.warn("Name, start date and end date are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        session_id: sessionId,
        name: form.name.trim(),
        start_date: toIso(form.start_date),
        end_date: toIso(form.end_date),
        weekly_fee: form.weekly_fee,
        sort_order: form.sort_order,
        // Sent always so clearing a field persists; ignored unless timingMode
        // is per_term. Empty string -> server stores null.
        start_time: form.start_time || "",
        end_time: form.end_time || "",
      };

      if (editingId) {
        const res = await updateTerm(editingId, payload);
        const s = res?.weeksSummary;
        if (s?.droppedUnavailable) {
          toast.warn(
            `${s.droppedUnavailable} week(s) you had marked off fall outside the new dates and were removed.`,
            { autoClose: 6000 }
          );
        } else {
          toast.success("Term updated");
        }
      } else {
        await createTerm(payload);
        toast.success("Term created");
      }

      setDialogOpen(false);
      await loadTerms(sessionId);
      if (editingId && selectedTerm?.id === editingId) {
        await loadWeeks({ ...selectedTerm, id: editingId });
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save term");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (term) => {
    confirmDialog({
      message: `Delete "${term.name}" and its week blocks?`,
      header: "Delete Term",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await deleteTerm(term.id);
          toast.success("Term deleted");
          if (selectedTerm?.id === term.id) {
            setSelectedTerm(null);
            setWeeks([]);
          }
          await loadTerms(sessionId);
        } catch (err) {
          toast.error(err?.response?.data?.message || "Failed to delete term");
        }
      },
    });
  };

  const availableCount = weeks.filter((w) => w.is_available).length;
  // Billable can be fractional under pro-rata billing, so it comes from the
  // server rather than being counted here.
  const billableCount =
    weekSummary?.billable_weeks != null ? Number(weekSummary.billable_weeks) : availableCount;
  const rate =
    weekSummary?.weekly_fee != null ? Number(weekSummary.weekly_fee) : null;

  const sessionOptions = sessions.map((s) => ({
    label: s.is_active ? `${s.name} (active)` : s.name,
    value: s.id,
  }));

  return (
    <div className="terms-manager">
      <ConfirmDialog />

      <div className="flex flex-wrap gap-2 align-items-center mb-3">
        <Dropdown
          value={sessionId}
          options={sessionOptions}
          onChange={(e) => setSessionId(e.value)}
          placeholder="Academic Session"
          className="w-16rem"
        />
        <Button
          label="Add Term"
          icon="pi pi-plus"
          className="p-button-sm"
          onClick={openCreate}
          disabled={!sessionId}
        />
      </div>

      <DataTable
        value={terms}
        loading={loadingTerms}
        selectionMode="single"
        selection={selectedTerm}
        onSelectionChange={(e) => e.value && loadWeeks(e.value)}
        dataKey="id"
        className="p-datatable-sm"
        emptyMessage="No terms yet. Add one to define its weeks."
      >
        <Column field="name" header="Term" />
        <Column header="Start" body={(r) => fmt(r.start_date)} />
        <Column header="End" body={(r) => fmt(r.end_date)} />
        {perTermTiming && (
          <Column
            header="Class Time"
            body={(r) =>
              r.start_time && r.end_time
                ? `${formatTime(r.start_time)} – ${formatTime(r.end_time)}`
                : "—"
            }
          />
        )}
        <Column
          header="Weeks"
          body={(r) => (
            <Tag
              severity={r.available_weeks === r.total_weeks ? "success" : "warning"}
              value={`${r.available_weeks} / ${r.total_weeks} available`}
            />
          )}
        />
        <Column
          header="Weekly Fee"
          body={(r) => (r.weekly_fee != null ? formatMoney(r.weekly_fee, currency) : "—")}
        />
        <Column
          header="Term Total"
          body={(r) =>
            r.weekly_fee != null
              ? formatMoney(Number(r.weekly_fee) * r.available_weeks, currency)
              : "—"
          }
        />
        <Column
          header=""
          style={{ width: "8rem" }}
          body={(r) => (
            <div className="flex gap-1">
              <Button
                icon="pi pi-calendar"
                className="p-button-text p-button-sm"
                tooltip="Manage weeks"
                onClick={() => loadWeeks(r)}
              />
              <Button
                icon="pi pi-pencil"
                className="p-button-text p-button-sm"
                onClick={() => openEdit(r)}
              />
              <Button
                icon="pi pi-trash"
                className="p-button-text p-button-sm p-button-danger"
                onClick={() => confirmDelete(r)}
              />
            </div>
          )}
        />
      </DataTable>

      {/* ── Week availability grid ── */}
      {selectedTerm && (
        <div className="mt-4">
          <div className="flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
            <div>
              <h3 className="m-0">{selectedTerm.name} — Week Availability</h3>
              <small className="text-color-secondary">
                Weeks follow the holiday calendar. Switch one to <strong>On</strong> or{" "}
                <strong>Off</strong> only to override it — a snow day, or a make-up week
                during a closure.
              </small>
            </div>
            <div className="flex gap-2 align-items-center">
              <Button
                label="Holiday Calendar"
                icon="pi pi-calendar"
                className="p-button-sm p-button-outlined"
                onClick={() => navigate("/index/holidays")}
              />
              <Button
                label="Clear overrides"
                className="p-button-sm p-button-text"
                onClick={() => setAllWeeks(null)}
                tooltip="Hand every week back to the holiday calendar"
              />
              <Button
                label="Save Weeks"
                icon="pi pi-check"
                className="p-button-sm"
                loading={savingWeeks}
                disabled={!Object.keys(dirtyWeeks).length}
                onClick={saveWeeks}
              />
            </div>
          </div>

          <div className="terms-manager__summary mb-3">
            <span>
              <strong>{availableCount}</strong> of {weeks.length} weeks available
            </span>
            {!!weekSummary?.partial_weeks && (
              <>
                <span className="terms-manager__dot" />
                <span>{weekSummary.partial_weeks} shortened by holidays</span>
              </>
            )}
            {!!weekSummary?.overridden_weeks && (
              <>
                <span className="terms-manager__dot" />
                <span>{weekSummary.overridden_weeks} manually overridden</span>
              </>
            )}
            {rate != null && (
              <>
                <span className="terms-manager__dot" />
                <span>
                  {formatMoney(rate, currency)} / week × <strong>{billableCount}</strong>{" "}
                  billable ={" "}
                  <strong>{formatMoney(rate * billableCount, currency)}</strong> per student
                  {weekSummary?.billing_mode === "pro_rata" && (
                    <span className="text-color-secondary"> (pro-rata)</span>
                  )}
                </span>
              </>
            )}
            {!!Object.keys(dirtyWeeks).length && (
              <>
                <span className="terms-manager__dot" />
                <span className="text-orange-500">unsaved changes</span>
              </>
            )}
          </div>

          {loadingWeeks ? (
            <div className="text-center p-4">
              <ProgressSpinner style={{ width: 40, height: 40 }} />
            </div>
          ) : (
            <div className="terms-manager__weeks">
              {weeks.map((w) => {
                const status = STATUS_TAG[w.status] || STATUS_TAG.full;
                const overridden = w.availability_override !== null &&
                  w.availability_override !== undefined;
                return (
                  <div
                    key={w.id}
                    className={`terms-manager__week ${w.is_available ? "is-on" : "is-off"}${
                      overridden ? " is-overridden" : ""
                    }`}
                  >
                    <div className="terms-manager__week-head">
                      <span className="terms-manager__week-no">Week {w.week_no}</span>
                      <Tag severity={status.severity} value={status.label} />
                    </div>

                    <div className="terms-manager__week-dates">
                      {fmt(w.week_start)} – {fmt(w.week_end)}
                    </div>

                    <div className="terms-manager__week-days">
                      {w.total_days > 0
                        ? `${w.teaching_days} of ${w.total_days} school days`
                        : "No school days"}
                    </div>

                    {!!w.holiday_names?.length && (
                      <div className="terms-manager__week-note" title={w.holiday_names.join(", ")}>
                        {w.holiday_names.join(", ")}
                      </div>
                    )}
                    {w.note && <div className="terms-manager__week-note">{w.note}</div>}

                    <SelectButton
                      className="terms-manager__week-override"
                      value={overridden ? w.availability_override : null}
                      options={OVERRIDE_OPTIONS}
                      optionLabel="label"
                      optionValue="value"
                      // SelectButton clears to undefined when the active choice
                      // is clicked again; treat that as "back to Auto".
                      onChange={(e) =>
                        setWeekOverride(w, e.value === undefined ? null : e.value)
                      }
                    />

                    {overridden && (
                      <div className="terms-manager__week-override-hint">
                        Overriding the calendar
                        {w.calendar_available !== w.is_available &&
                          ` (calendar says ${w.calendar_available ? "on" : "off"})`}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Term form ── */}
      <Dialog
        header={editingId ? "Edit Term" : "Add Term"}
        visible={dialogOpen}
        onHide={() => setDialogOpen(false)}
        style={{ width: "34rem" }}
        modal
      >
        <div className="grid formgrid">
          <div className="field col-12">
            <label htmlFor="term-name">Term Name</label>
            <InputText
              id="term-name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="w-full"
              placeholder="e.g. Autumn Term"
            />
          </div>
          <div className="field col-12 md:col-6">
            <label htmlFor="term-start">Start Date</label>
            <Calendar
              id="term-start"
              value={form.start_date}
              onChange={(e) => setForm((f) => ({ ...f, start_date: e.value }))}
              dateFormat="dd M yy"
              showIcon
              className="w-full"
            />
          </div>
          <div className="field col-12 md:col-6">
            <label htmlFor="term-end">End Date</label>
            <Calendar
              id="term-end"
              value={form.end_date}
              onChange={(e) => setForm((f) => ({ ...f, end_date: e.value }))}
              dateFormat="dd M yy"
              showIcon
              minDate={form.start_date || undefined}
              className="w-full"
            />
          </div>
          <div className="field col-12 md:col-6">
            <label htmlFor="term-fee">Weekly Fee</label>
            <InputNumber
              id="term-fee"
              value={form.weekly_fee}
              onValueChange={(e) => setForm((f) => ({ ...f, weekly_fee: e.value }))}
              mode="decimal"
              minFractionDigits={0}
              maxFractionDigits={2}
              prefix={currency ? `${currency} ` : undefined}
              className="w-full"
            />
            <small className="text-color-secondary">
              Default rate; a student's own weekly fee overrides it.
            </small>
          </div>
          <div className="field col-12 md:col-6">
            <label htmlFor="term-order">Display Order</label>
            <InputNumber
              id="term-order"
              value={form.sort_order}
              onValueChange={(e) => setForm((f) => ({ ...f, sort_order: e.value ?? 0 }))}
              className="w-full"
            />
          </div>

          {perTermTiming && (
            <>
              <div className="field col-12 md:col-6">
                <label htmlFor="term-start-time">Class Start Time</label>
                <InputText
                  id="term-start-time"
                  type="time"
                  value={form.start_time}
                  onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))}
                  className="w-full"
                />
              </div>
              <div className="field col-12 md:col-6">
                <label htmlFor="term-end-time">Class End Time</label>
                <InputText
                  id="term-end-time"
                  type="time"
                  value={form.end_time}
                  onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))}
                  className="w-full"
                />
              </div>
              <div className="field col-12">
                <small className="text-color-secondary">
                  Every class in this term uses this time (your school's timing mode is
                  “Per term”). Leave blank to fall back to each class's own schedule time.
                </small>
              </div>
            </>
          )}
        </div>

        {editingId && (
          <small className="text-color-secondary block mb-3">
            Changing the dates regenerates the week blocks. Weeks that still exist keep their
            on/off setting.
          </small>
        )}

        <div className="flex justify-content-end gap-2">
          <Button
            label="Cancel"
            className="p-button-text"
            onClick={() => setDialogOpen(false)}
          />
          <Button label="Save" icon="pi pi-check" loading={saving} onClick={saveTerm} />
        </div>
      </Dialog>
    </div>
  );
};

export default TermsManager;
